import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code as CodeIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
  Undo,
  Redo,
  Image as ImageIcon,
  Link as LinkIcon,
  Title as TitleIcon,
} from '@mui/icons-material';

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  readOnly?: boolean;
}

/**
 * TipTapEditor — MUI-styled rich text editor wrapper.
 * Toolbar uses MUI IconButtons, editor area is styled to match the dashboard theme.
 */
export default function TipTapEditor({
  content,
  onChange,
  placeholder = 'Start writing...',
  minHeight = 200,
  readOnly = false,
}: TipTapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      prevContentRef.current = html;
      onChange(html);
    },
  });

  // Sync content from external prop changes (e.g., API data arriving after mount)
  const prevContentRef = useRef(content);
  useEffect(() => {
    if (editor && content !== prevContentRef.current) {
      // Only update the editor if the incoming content differs from what it currently has
      if (content !== editor.getHTML()) {
        editor.commands.setContent(content, { emitUpdate: false });
      }
      prevContentRef.current = content;
    }
  }, [content, editor]);

  if (!editor) return null;

  const handleAddImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleAddLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter link URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Toolbar */}
      {!readOnly && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              backgroundColor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Headings */}
            <Tooltip title="Heading 2">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
              >
                <TitleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Heading 3">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
              >
                <TitleIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Text formatting */}
            <Tooltip title="Bold">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                color={editor.isActive('bold') ? 'primary' : 'default'}
              >
                <FormatBold fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                color={editor.isActive('italic') ? 'primary' : 'default'}
              >
                <FormatItalic fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Strikethrough">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                color={editor.isActive('strike') ? 'primary' : 'default'}
              >
                <FormatStrikethrough fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Inline Code">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleCode().run()}
                color={editor.isActive('code') ? 'primary' : 'default'}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Lists */}
            <Tooltip title="Bullet List">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                color={editor.isActive('bulletList') ? 'primary' : 'default'}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                color={editor.isActive('orderedList') ? 'primary' : 'default'}
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Block elements */}
            <Tooltip title="Block Quote">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                color={editor.isActive('blockquote') ? 'primary' : 'default'}
              >
                <FormatQuote fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Horizontal Rule">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
              >
                <HorizontalRule fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Media */}
            <Tooltip title="Insert Image">
              <IconButton size="small" onClick={handleAddImage}>
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert Link">
              <IconButton
                size="small"
                onClick={handleAddLink}
                color={editor.isActive('link') ? 'primary' : 'default'}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Undo/Redo */}
            <Tooltip title="Undo">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
              >
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Redo">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
              >
                <Redo fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Editor Content */}
      <Box
        sx={{
          minHeight,
          px: 2,
          py: 1.5,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: minHeight - 24,
            '& p': { margin: '0 0 0.75em' },
            '& h2': { fontSize: '1.5rem', fontWeight: 700, margin: '1.5em 0 0.5em' },
            '& h3': { fontSize: '1.25rem', fontWeight: 600, margin: '1.25em 0 0.5em' },
            '& h4': { fontSize: '1.1rem', fontWeight: 600, margin: '1em 0 0.5em' },
            '& ul, & ol': { paddingLeft: '1.5em', margin: '0 0 0.75em' },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              paddingLeft: '1em',
              margin: '0.75em 0',
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 1,
              margin: '0.75em 0',
            },
            '& a': { color: 'primary.main', textDecoration: 'underline' },
            '& code': {
              backgroundColor: 'grey.100',
              borderRadius: '3px',
              padding: '0.1em 0.3em',
              fontSize: '0.9em',
            },
            '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', margin: '1em 0' },
            '& .is-editor-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
}
